<script setup lang="ts">
import type {
  TpgSelectEvent,
} from '@tpg/grammar-web';
import {
  tpgIconClose,
  tpgIconContentCopy,
  tpgIconCopyAll,
  tpgIconDelete,
  tpgIconEdit,
  tpgIconMap,
  tpgIconMoreVert,
  tpgIconMoveUp,
} from '@tpg/grammar-web-icons';
import type { Feature } from 'ol';
import type { Geometry } from 'ol/geom';
import { storeToRefs } from 'pinia';
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  toRaw,
} from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarDivider,
  GrammarMenuItem,
  GrammarOverlayCard,
  GrammarQuaternaryButton,
  GrammarQuaternaryMenuButton,
} from '@nora-monorepo/grammar-ui';
import { DrawLayerTypeEnum } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import {
  appLogger,
  MapLoggingContext,
  stringifyError,
  useConfirmModalStore,
  useToastStore,
} from '@nora-monorepo/ui-shared';

import DrawLayerPicker from '#nora-ui/map/components/drawtools/DrawLayerPicker.vue';
import { useDrawFeatureSelectionStore } from '#nora-ui/map/draw-feature-selection/draw-feature-selection-store';
import {
  getFeatureNameByDrawToolType,
  isDrawToolType,
} from '#nora-ui/map/drawtools/draw-tool-utils';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { operationCan } from '#nora-ui/utils/permission';

interface DrawFeatureSelectionViewProps {
  mapId: string;
}

enum MenuOption {
  CopyToDrawLayer = 'copyToDrawLayer',
  Deselect = 'deselect',
  MoveToDrawLayer = 'moveToDrawLayer',
}

const props = defineProps<DrawFeatureSelectionViewProps>();
const { t } = useI18n();
const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(props.mapId);
const selectionStore = useDrawFeatureSelectionStore(props.mapId);
const confirmModalStore = useConfirmModalStore();
const toastStore = useToastStore();
const drawToolUI = map.drawToolUI;

const showDrawLayerPicker = ref(false);
const currentDrawLayerAction = ref<
  MenuOption.CopyToDrawLayer | MenuOption.MoveToDrawLayer
>();

const {
  candidateFeatures,
  isCandidateSelectionOpen,
  selectedFeature,
} = storeToRefs(selectionStore);

const selectedDrawLayerId = computed<string | undefined>(() => {
  const drawLayerId = selectedFeature.value?.get('drawLayerId');

  return isNonEmptyString(drawLayerId) ? drawLayerId : undefined;
});

const selectedDrawLayer = computed(() => {
  if (!selectedDrawLayerId.value) {
    return undefined;
  }

  return map.state.draw.getDrawLayerById(selectedDrawLayerId.value);
});

const selectedFeatureName = computed(() => {
  const feature = selectedFeature.value;
  if (!feature) {
    return '';
  }

  return getFeatureDisplayName(feature);
});

const canModifySelectedFeature = computed(() => {
  const drawLayer = selectedDrawLayer.value;

  return drawLayer !== undefined
    && drawLayer.type === DrawLayerTypeEnum.USER
    && !drawLayer.locked
    && operationCan('update:draw-layer.feature', drawLayer.operationId);
});

const drawLayerPickerHeading = computed(() => {
  switch (currentDrawLayerAction.value) {
    case MenuOption.CopyToDrawLayer:
      return t('drawFeatureSelection.copyToDrawLayer');
    case MenuOption.MoveToDrawLayer:
      return t('drawFeatureSelection.moveToDrawLayer');
    default:
      return '';
  }
});

const candidateItems = computed(() => candidateFeatures.value.map(feature => ({
  feature,
  featureName: getFeatureDisplayName(feature),
  drawLayerName: getDrawLayerName(feature),
})));

let unregisterDeleteConfirmationHandler: (() => void) | null = null;

onMounted(() => {
  unregisterDeleteConfirmationHandler = selectionStore.registerDeleteConfirmationHandler(onDeleteClick);
});

onUnmounted(() => {
  unregisterDeleteConfirmationHandler?.();
});

function getFeatureDisplayName(feature: Feature<Geometry>): string {
  const name = feature.get('name');
  if (typeof name === 'string' && name.trim().length > 0) {
    return name.trim();
  }

  const drawToolType = feature.get('drawToolType');
  if (isDrawToolType(drawToolType)) {
    const toolName = getFeatureNameByDrawToolType(drawToolType);
    if (toolName.length > 0) {
      return toolName;
    }
  }

  return t('drawFeatureSelection.unnamedFeature');
}

function getDrawLayerName(feature: Feature<Geometry>): string {
  const drawLayerId = feature.get('drawLayerId');
  if (!isNonEmptyString(drawLayerId)) {
    return '';
  }

  return map.state.draw.getDrawLayerById(drawLayerId)?.name ?? '';
}

function onCloseClick(): void {
  selectionStore.deselectFeature();
}

function onCloseCandidateSelection(): void {
  selectionStore.clearCandidateSelection();
}

function onCandidateSelected(feature: Feature<Geometry>): void {
  selectionStore.selectCandidate(toRaw(feature));
}

function onEditClick(): void {
  const feature = selectedFeature.value;
  if (!feature || !canModifySelectedFeature.value) {
    return;
  }

  selectionStore.deselectFeature();
  drawToolUI.editFeature(toRaw(feature));
}

function onDeleteClick(): void {
  if (!canModifySelectedFeature.value) {
    return;
  }

  confirmModalStore.showConfirmModal({
    headerText: t('drawFeatureSelection.deleteFeature'),
    bodyText: t('drawFeatureSelection.deleteFeatureConfirmation'),
    isDelete: true,
    onConfirm: async () => {
      await runMutation(
        () => selectionStore.deleteSelectedFeature(),
        'delete',
      );
    },
  });
}

function onOpenDrawLayerMenuClick(
  menuOption: MenuOption.CopyToDrawLayer | MenuOption.MoveToDrawLayer,
): void {
  if (!canModifySelectedFeature.value) {
    return;
  }

  currentDrawLayerAction.value = menuOption;
  showDrawLayerPicker.value = true;
}

function onCloseDrawLayerMenuClick(): void {
  showDrawLayerPicker.value = false;
  currentDrawLayerAction.value = undefined;
}

async function copyFeatureToDrawLayer(options: {
  drawLayerId: string;
  removeOriginal: boolean;
}): Promise<void> {
  const feature = selectedFeature.value;
  if (!feature) {
    return;
  }

  const featureCopy = feature.clone();
  featureCopy.setId(undefined);
  featureCopy.set('id', undefined);

  const savedFeature = await map.drawToolManager.saveFeature(featureCopy, options.drawLayerId);
  map.drawToolManager.vectorSource.addFeature(savedFeature);

  if (options.removeOriginal) {
    await selectionStore.deleteSelectedFeature();
  }

  selectionStore.deselectFeature();
}

async function onDrawLayerSelected(drawLayerId: string): Promise<void> {
  const currentAction = currentDrawLayerAction.value;
  onCloseDrawLayerMenuClick();

  if (!currentAction) {
    return;
  }

  await runMutation(
    async () => {
      if (currentAction === MenuOption.CopyToDrawLayer) {
        await copyFeatureToDrawLayer({
          drawLayerId,
          removeOriginal: false,
        });
        return;
      }

      await copyFeatureToDrawLayer({
        drawLayerId,
        removeOriginal: true,
      });
    },
    currentAction,
  );
}

async function onDuplicateClick(): Promise<void> {
  if (!canModifySelectedFeature.value) {
    return;
  }

  await runMutation(
    () => selectionStore.duplicateSelectedFeature(),
    'duplicate',
  );
}

async function runMutation(
  mutation: () => Promise<void>,
  action: 'copyToDrawLayer' | 'delete' | 'duplicate' | 'moveToDrawLayer',
): Promise<void> {
  try {
    await mutation();
  }
  catch (error) {
    appLogger.error({
      message: 'Could not update selected draw feature',
      context: {
        map: new MapLoggingContext({ mapId: props.mapId }),
        action,
        featureId: selectedFeature.value?.getId(),
      },
      sensitiveContext: { error },
    });
    toastStore.addError({
      message: t('drawFeatureSelection.updateFailedTitle'),
      detail: stringifyError(error),
    });
  }
}

function onMenuSelect(event: TpgSelectEvent): void {
  if (typeof event.value !== 'string') {
    return;
  }

  switch (event.value) {
    case MenuOption.CopyToDrawLayer:
      onOpenDrawLayerMenuClick(MenuOption.CopyToDrawLayer);
      break;
    case MenuOption.Deselect:
      selectionStore.deselectFeature();
      break;
    case MenuOption.MoveToDrawLayer:
      onOpenDrawLayerMenuClick(MenuOption.MoveToDrawLayer);
      break;
  }
}
</script>

<template>
  <GrammarOverlayCard
    v-if="isCandidateSelectionOpen"
    :heading-label="t('drawFeatureSelection.selectFeature')"
  >
    <template #header-trailing-buttons>
      <GrammarQuaternaryButton
        :icon-template="tpgIconClose"
        @click="onCloseCandidateSelection"
      />
    </template>
    <div class="max-h-43.75 overflow-y-scroll">
      <GrammarMenuItem
        v-for="candidate in candidateItems"
        :key="candidate.feature.getId()"
        :label="candidate.featureName"
        :secondary-label="candidate.drawLayerName"
        @select="onCandidateSelected(candidate.feature)"
      />
    </div>
  </GrammarOverlayCard>

  <div
    v-if="showDrawLayerPicker"
    class="flex flex-col justify-center items-center pb-1"
  >
    <GrammarOverlayCard
      style="z-index: 100"
      :leading-icon-template="tpgIconMap"
      :heading-label="drawLayerPickerHeading"
    >
      <template #header-trailing-buttons>
        <GrammarQuaternaryButton
          :icon-template="tpgIconClose"
          @click="onCloseDrawLayerMenuClick"
        />
      </template>

      <div class="flex flex-col max-h-43.75 overflow-y-scroll">
        <DrawLayerPicker
          :map-id="props.mapId"
          :selected-draw-layer-id="selectedDrawLayerId"
          @select-draw-layer="onDrawLayerSelected"
        />
      </div>
    </GrammarOverlayCard>
  </div>

  <GrammarOverlayCard
    v-if="selectedFeature && drawToolUI.activeDrawTool.value === null"
    :heading-label="selectedFeatureName"
    :label="selectedDrawLayer?.name"
  >
    <template #header-trailing-buttons>
      <GrammarQuaternaryButton
        :icon-template="tpgIconClose"
        @click="onCloseClick"
      />
    </template>
    <div class="flex flex-row items-center justify-center gap-2 p-2">
      <GrammarQuaternaryButton
        v-if="canModifySelectedFeature"
        :label="t('drawFeatureSelection.edit')"
        :icon-template="tpgIconEdit"
        icon-position="top"
        @click="onEditClick"
      />
      <GrammarDivider
        v-if="canModifySelectedFeature"
        orientation="vertical"
      />
      <GrammarQuaternaryButton
        v-if="canModifySelectedFeature"
        :label="t('drawFeatureSelection.duplicate')"
        :icon-template="tpgIconContentCopy"
        icon-position="top"
        @click="onDuplicateClick"
      />
      <GrammarDivider
        v-if="canModifySelectedFeature"
        orientation="vertical"
      />
      <GrammarQuaternaryButton
        v-if="canModifySelectedFeature"
        :label="t('drawFeatureSelection.delete')"
        :icon-template="tpgIconDelete"
        icon-position="top"
        @click="onDeleteClick"
      />
      <GrammarDivider
        v-if="canModifySelectedFeature"
        orientation="vertical"
      />
      <GrammarQuaternaryMenuButton
        :icon-template="tpgIconMoreVert"
        @tpg-select="onMenuSelect"
      >
        <GrammarMenuItem
          v-if="canModifySelectedFeature"
          :leading-icon-template="tpgIconCopyAll"
          :label="t('drawFeatureSelection.copyToDrawLayer')"
          :value="MenuOption.CopyToDrawLayer"
        />
        <GrammarMenuItem
          v-if="canModifySelectedFeature"
          :leading-icon-template="tpgIconMoveUp"
          :label="t('drawFeatureSelection.moveToDrawLayer')"
          :value="MenuOption.MoveToDrawLayer"
        />
        <GrammarMenuItem
          :label="t('drawFeatureSelection.deselect')"
          :value="MenuOption.Deselect"
        />
      </GrammarQuaternaryMenuButton>
    </div>
  </GrammarOverlayCard>
</template>
